/**
 * Wymiar urlopu wypoczynkowego jako reguła (Fala 1 po teście z 2026-09-26, #6).
 *
 * Do tej pory pula urlopu była liczbą ze słownika (26 dni) — bez etatu, bez
 * proporcji roku ustania i bez godzin. Ekwiwalent w rozliczeniu końcowym
 * wychodził z niej za cały rok, choć osoba pracowała trzy miesiące. Reguła:
 *
 *  - wymiar dla etatu: art. 154 § 2 k.p. — proporcjonalnie do wymiaru czasu
 *    pracy, niepełny dzień w górę,
 *  - rok ustania stosunku pracy: art. 155(1) § 1 pkt 1 — proporcjonalnie do
 *    okresu przepracowanego w tym roku; kalendarzowy miesiąc pracy to 1/12
 *    wymiaru, niepełny miesiąc i niepełny dzień zaokrągla się w górę, a wynik
 *    nie przekracza wymiaru rocznego (art. 155(2a) § 1–3),
 *  - jednostką są GODZINY: dzień urlopu odpowiada 8 godzinom (art. 154(2)
 *    § 2), więc wymiar w dniach mnożymy przez 8, a dzień urlopu osoby na
 *    niepełnym etacie zużywa 8 × etat godzin,
 *  - etat z HISTORII warunków także przy zatrudnieniu trwającym (Fala 7):
 *    przy zmianie etatu w roku każdy miesiąc kalendarzowy ma etat ze swojego
 *    ostatniego dnia, a wymiar to suma dwunastych części — ta sama arytmetyka
 *    co w roku ustania. Dawniej trwające zatrudnienie brało etat z 31.12,
 *    więc aneks na 1/2 etatu od listopada ścinał cały rok do 13 dni.
 *
 * Granice nazwane, a nie udawane: wymiaru z art. 154 § 1 (20 albo 26 dni
 * zależnie od stażu z art. 155) system nie wyprowadza — podstawą jest słownik
 * typów urlopu, a wynik trzeba potwierdzić w aktach. Nie liczymy urlopu
 * pierwszej pracy (art. 153 § 1), proporcji u nowego pracodawcy w roku
 * zmiany pracy (art. 155(1) § 1 pkt 2), urlopu uzupełniającego (art. 158)
 * ani urlopu zaległego z lat poprzednich.
 */

/** Godziny odpowiadające jednemu dniu urlopu (art. 154(2) § 2 k.p.). */
export const LEAVE_DAY_HOURS = 8

/** Etat obowiązujący od dnia `from` do dnia przed kolejnym wpisem historii. */
export interface LeaveFteSpan {
  readonly from: string
  readonly fteBp: number
}

export interface LeaveEntitlementInput {
  /** Wymiar z art. 154 § 1 (20 albo 26 dni) — u nas ze słownika typów urlopu. */
  readonly baseDays: number
  readonly year: number
  /** Pierwszy dzień zatrudnienia u tego pracodawcy. */
  readonly employedFrom: string
  /** Ostatni dzień zatrudnienia; `null` = zatrudnienie trwa. */
  readonly employedTo: string | null
  /** Historia etatu. Pusta = pełny etat (wywołujący ostrzega, że to założenie). */
  readonly fteSpans: readonly LeaveFteSpan[]
}

export interface LeaveEntitlement {
  /** Wymiar w dniach urlopu (art. 154, po proporcji z art. 155(1)). */
  readonly days: number
  /** Ten sam wymiar w godzinach: dni × 8 (art. 154(2) § 2). */
  readonly hours: number
  /** Wymiar roczny dla etatu z ostatniego dnia okresu — punkt odniesienia noty. */
  readonly fullYearDays: number
  /** Miesiące proporcji z art. 155(1); `null` = pełny wymiar roku. */
  readonly proportionalMonths: number | null
}

const FULL_TIME_BP = 10_000

/** Etat w dniu `day` z historii; bez historii — pełny etat. */
export function fteOn(spans: readonly LeaveFteSpan[], day: string): number {
  const sorted = spans.toSorted((left, right) => (left.from < right.from ? -1 : 1))
  const inForce = sorted.filter((span) => span.from <= day)
  return (inForce.at(-1) ?? sorted[0])?.fteBp ?? FULL_TIME_BP
}

/** Wymiar roczny dla etatu: art. 154 § 2, niepełny dzień w górę. */
function annualDays(baseDays: number, fteBp: number): number {
  return Math.ceil((baseDays * fteBp) / FULL_TIME_BP)
}

function lastDayOfMonth(year: number, month: number): string {
  const day = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Wymiar z miesięcy `firstMonth`–`lastMonth` roku, gdy każdy miesiąc ma etat
 * ze swojego ostatniego dnia zatrudnienia (najpóźniej `lastDay`): kalendarzowy
 * miesiąc to 1/12 wymiaru rocznego dla tego etatu, suma w górę do pełnego dnia
 * (art. 155(2a) § 2), a wynik nie większy niż najwyższy wymiar roczny
 * z tych miesięcy (§ 3). Przy stałym etacie i dwunastu miesiącach daje
 * dokładnie wymiar roczny.
 */
function monthlyDays(
  input: LeaveEntitlementInput,
  firstMonth: number,
  lastMonth: number,
  lastDay: string,
): number {
  let sumOfAnnual = 0
  let cap = 0
  for (let month = firstMonth; month <= lastMonth; month += 1) {
    const monthEnd = lastDayOfMonth(input.year, month)
    const lastEmployedDay = monthEnd < lastDay ? monthEnd : lastDay
    const annual = annualDays(input.baseDays, fteOn(input.fteSpans, lastEmployedDay))
    sumOfAnnual += annual
    cap = Math.max(cap, annual)
  }
  return Math.min(Math.ceil(sumOfAnnual / 12), cap)
}

/**
 * Wymiar urlopu wypoczynkowego w roku `year`. Proporcja z art. 155(1) § 1
 * pkt 1 działa w roku USTANIA zatrudnienia: miesiąc kalendarzowy, w którym
 * osoba choć jeden dzień pracowała, liczy się jako pełny (art. 155(2a) § 1).
 * Najpierw etat (art. 154 § 2, w górę), potem proporcja — dla każdego
 * miesiąca z etatem, który obowiązywał w jego ostatnim dniu zatrudnienia.
 *
 * Zatrudnienie trwające przez cały rok ma wymiar roczny, ale etat także
 * bierze się miesiącami z historii (wiążąca poprawka przeglądu P7: „etat
 * z jednego dnia"). Etat sprzed pierwszego wpisu historii to etat z tego
 * wpisu. Zatrudnienie, które ustało przed rokiem albo zaczyna się po nim,
 * w tym roku urlopu nie daje.
 */
export function leaveEntitlement(input: LeaveEntitlementInput): LeaveEntitlement {
  const yearStart = `${input.year}-01-01`
  const yearEnd = `${input.year}-12-31`
  const from = input.employedFrom > yearStart ? input.employedFrom : yearStart
  const endsThisYear = input.employedTo !== null && input.employedTo.slice(0, 4) === String(input.year)
  const to = endsThisYear ? input.employedTo! : yearEnd
  const fullYearDays = annualDays(input.baseDays, fteOn(input.fteSpans, to))

  const endedBefore = input.employedTo !== null && input.employedTo < yearStart
  if (endedBefore || input.employedFrom > yearEnd) {
    return { days: 0, hours: 0, fullYearDays, proportionalMonths: 0 }
  }

  if (!endsThisYear) {
    const days = monthlyDays(input, 1, 12, yearEnd)
    return {
      days,
      hours: days * LEAVE_DAY_HOURS,
      fullYearDays,
      proportionalMonths: null,
    }
  }
  if (from > to) {
    return { days: 0, hours: 0, fullYearDays, proportionalMonths: 0 }
  }

  const firstMonth = Number(from.slice(5, 7))
  const lastMonth = Number(to.slice(5, 7))
  const months = lastMonth - firstMonth + 1
  /* Kalendarzowy miesiąc = 1/12 wymiaru, niepełny dzień w górę (art. 155(2a) § 2). */
  const days = monthlyDays(input, firstMonth, lastMonth, to)
  return {
    days,
    hours: days * LEAVE_DAY_HOURS,
    fullYearDays,
    proportionalMonths: months,
  }
}

/** Godziny, które zużywa jeden dzień urlopu przy danym etacie: 8 × etat. */
export function leaveHoursPerDay(fteBp: number): number {
  return (LEAVE_DAY_HOURS * fteBp) / FULL_TIME_BP
}

/**
 * Godziny zużyte wnioskami urlopowymi: dzień urlopu to 8 × etat godzin
 * w dniu, w którym wniosek się zaczyna (art. 154(2) k.p.). `days` to dni
 * robocze wniosku już przycięte przez wywołującego (np. do dnia ustania).
 */
export function leaveHoursUsed(
  requests: readonly { readonly from: string; readonly days: number }[],
  fteSpans: readonly LeaveFteSpan[],
): number {
  return requests.reduce(
    (sum, request) => sum + request.days * leaveHoursPerDay(fteOn(fteSpans, request.from)),
    0,
  )
}

// ---------------------------------------------------------------------------
// Kontrakt portalu i kadr (Fala 7, D102)
// ---------------------------------------------------------------------------

/**
 * Stan prawa do urlopu według rodzaju umowy: urlop wypoczynkowy przysługuje
 * pracownikowi (art. 152 § 1 k.p.). Przy zleceniu, dziele i B2B pula jest
 * pusta, a portal mówi „nie dotyczy" zamiast pokazywać zero (D102).
 */
export const LEAVE_ENTITLEMENT_KINDS = ['kodeks_pracy', 'nie_dotyczy'] as const
export type LeaveEntitlementKind = (typeof LEAVE_ENTITLEMENT_KINDS)[number]

/**
 * Kody odmowy wniosku urlopowego (POST `/me/leaves`). Każdy powód odmowy
 * z walidacji wniosku ma kod z tej listy, a ekran — zdanie dla każdego kodu
 * w obu językach (test „każdy kod ma tłumaczenie").
 */
export const LEAVE_REQUEST_ERROR_CODES = [
  'UNKNOWN_TYPE',
  'LEAVE_NOT_APPLICABLE',
  'OUTSIDE_EMPLOYMENT',
  'NO_WORKING_DAYS',
  'OVERLAP',
  'INSUFFICIENT_BALANCE',
] as const
export type LeaveRequestErrorCode = (typeof LEAVE_REQUEST_ERROR_CODES)[number]
